import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { consoleApi } from '@/lib/consoleApi';
import { useAuth } from '@/providers/AuthProvider';
import { roleSatisfies } from '@/lib/roleRank';
import { cohortConcordance } from '@/lib/periReview';
import { blockingOpen, daysUntil, relativeDayLabel, openFindings, openP0 } from '@/lib/betaConsole';
import { AdminPage } from '@/admin';
import { ProtocolTab } from './beta/ProtocolTab';
import { TimelineTab } from './beta/TimelineTab';
import { ReadinessTab } from './beta/ReadinessTab';
import { CohortTab } from './beta/CohortTab';
import { FindingsTab } from './beta/FindingsTab';
import { InterviewsTab } from './beta/InterviewsTab';

type TabId = 'protocol' | 'timeline' | 'readiness' | 'cohort' | 'findings' | 'interviews';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'protocol', label: 'Protocol' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'readiness', label: 'Readiness' },
  { id: 'cohort', label: 'Cohort' },
  { id: 'findings', label: 'Findings' },
  { id: 'interviews', label: 'Interviews' },
];

/** Perimenopause closed-beta protocol and running console.
 *
 *  Ported from docs/presentations/bioweave_peri_beta_console_1.html, which
 *  persisted through the Artifacts `window.storage` API with no localStorage
 *  fallback — outside that runtime a refresh wiped the cohort, the reviews and
 *  the findings. Here state is server-side and the surface inherits the
 *  console's existing auth rather than sitting on an unauthenticated page.
 *
 *  Clinical review is deliberately NOT a tab: it already exists at /review/peri
 *  with the blind enforced server-side. The tab bar links out to it. */
export function BetaConsolePage() {
  const [tab, setTab] = useState<TabId>('protocol');
  const { role } = useAuth();
  const canEdit = roleSatisfies(role ?? '', 'admin');

  const gates = useQuery({ queryKey: ['beta-gates'], queryFn: () => adminApi.beta.gates.list() });
  const timeline = useQuery({ queryKey: ['beta-timeline'], queryFn: () => adminApi.beta.timeline.list() });
  const findings = useQuery({ queryKey: ['beta-findings'], queryFn: () => adminApi.beta.findings.list() });
  const queue = useQuery({ queryKey: ['peri-review-queue'], queryFn: () => consoleApi.periReview.queue() });
  // The rubric is the source of the primary metric. The protocol page states what
  // it says rather than carrying a second copy that can drift out of agreement.
  const rubric = useQuery({ queryKey: ['peri-review-rubric'], queryFn: () => consoleApi.periReview.rubric() });

  const gateRows = gates.data?.gates ?? [];
  const blocking = blockingOpen(gateRows);
  const findingRows = findings.data ?? [];
  const participants = queue.data?.participants ?? [];
  const cohort = queue.data?.cohort_status;
  const metric = cohortConcordance(participants);

  const freeze = timeline.data?.find((d) => d.id === 'd2');
  const onboarding = timeline.data?.find((d) => d.id === 'd7');
  const freezeDays = daysUntil(freeze?.date);
  const onboardDays = daysUntil(onboarding?.date);

  // Preflight is only a verdict once the gates have actually loaded. An unloaded
  // registry must not read as "all clear" — that is the fail-quiet the source
  // page was explicitly modelled against.
  const preflightKnown = gates.isSuccess;

  return (
    <AdminPage
      title="Perimenopause closed beta"
      description="Protocol and running console. The beta asks one question: does the board's reasoning produce clinically sound, safe and useful output when it meets real users and real data? Everything here serves that question."
    >
      <div
        data-testid="beta-preflight"
        className={`mb-13 rounded p-13 font-mono text-[12px] font-bold uppercase tracking-wide ${
          !preflightKnown
            ? 'bg-surface-sunken text-text-secondary'
            : blocking.length
              ? 'bg-flagged text-white'
              : 'bg-success text-white'
        }`}
      >
        {!preflightKnown ? (
          <>[beta] preflight unknown — gates not loaded</>
        ) : blocking.length ? (
          <>
            [beta] preflight fail — {blocking.length} blocking gate{blocking.length > 1 ? 's' : ''} open
            <span className="ml-4 border-l border-current/40 pl-4 font-normal normal-case opacity-85">
              {freezeDays !== null ? `Build freeze ${relativeDayLabel(freezeDays)} · ` : ''}
              {onboardDays !== null ? `onboarding ${relativeDayLabel(onboardDays)} · ` : ''}
              not cleared to recruit past screening
            </span>
          </>
        ) : (
          <>
            [beta] preflight pass — all blocking gates clear
            <span className="ml-4 border-l border-current/40 pl-4 font-normal normal-case opacity-85">
              Cleared to onboard{onboardDays !== null ? ` · onboarding ${relativeDayLabel(onboardDays)}` : ''}
            </span>
          </>
        )}
      </div>

      <div className="mb-13 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Vital
          k="Blocking gates open"
          v={preflightKnown ? String(blocking.length) : '—'}
          n={preflightKnown ? `${gateRows.filter((g) => g.status === 'done').length}/${gateRows.length} done` : 'not loaded'}
          alarm={preflightKnown && blocking.length > 0}
        />
        {/* A passed milestone reads as "11d ago", never "-11" — a negative count in
            a metric tile obscures the one thing worth noticing, that the date went. */}
        <Vital
          k="Build freeze"
          v={freezeDays !== null ? (freezeDays < 0 ? `${-freezeDays}d ago` : String(freezeDays)) : '—'}
          n={freeze?.date ?? 'not loaded'}
          alarm={freezeDays !== null && freezeDays < 0}
        />
        <Vital
          k="Cohort consented"
          v={cohort ? `${cohort.size}/${cohort.target.size_max}` : '—'}
          n={cohort ? `${cohort.blinded}/${cohort.target.blinded_max} blinded` : 'not loaded'}
        />
        <Vital k="Baseline reviews" v={`${metric.scored}/${participants.length || '—'}`} n="scored against the rubric" />
        {/* Subtitle names the rubric, not a paraphrase of its pass rule — the
            July copy said "no safety issue + substantive", values the rubric
            does not use. */}
        <Vital
          k="Primary metric"
          v={metric.scored ? `${metric.passing}/${metric.scored}` : '—'}
          n={rubric.data ? `rubric v${rubric.data.version}` : 'rubric not loaded'}
          good={metric.pct !== null && metric.pct >= 80}
        />
        <Vital
          k="Open P0 findings"
          v={findings.isSuccess ? String(openP0(findingRows).length) : '—'}
          n={findings.isSuccess ? `${openFindings(findingRows).length} open total` : 'not loaded'}
          alarm={findings.isSuccess && openP0(findingRows).length > 0}
        />
      </div>

      <nav className="mb-13 flex gap-1 overflow-x-auto border-b border-border" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            data-testid={`beta-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-body-sm ${
              tab === t.id
                ? 'border-accent font-semibold text-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {t.label}
          </button>
        ))}
        <Link
          to="/review/peri"
          className="-mb-px whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-body-sm text-text-secondary hover:text-text-primary"
          title="The blinded clinical review is its own surface — the reveal gate is enforced server-side"
        >
          Clinical review ↗
        </Link>
      </nav>

      {tab === 'protocol' ? <ProtocolTab metric={metric} rubric={rubric.data} /> : null}
      {tab === 'timeline' ? <TimelineTab canEdit={canEdit} /> : null}
      {tab === 'readiness' ? <ReadinessTab canEdit={canEdit} /> : null}
      {tab === 'cohort' ? <CohortTab /> : null}
      {tab === 'findings' ? <FindingsTab canEdit={canEdit} blockingGates={blocking.length} /> : null}
      {tab === 'interviews' ? <InterviewsTab /> : null}
    </AdminPage>
  );
}

function Vital({ k, v, n, alarm, good }: { k: string; v: string; n: string; alarm?: boolean; good?: boolean }) {
  return (
    <div className="rounded border border-border bg-surface p-13">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wide text-text-muted">{k}</div>
      <div className={`font-mono text-[21px] font-bold leading-none ${alarm ? 'text-flagged' : good ? 'text-success' : 'text-text-primary'}`}>
        {v}
      </div>
      <div className="mt-1 text-[11px] text-text-muted">{n}</div>
    </div>
  );
}
